import { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import type { AdminContentSubmissionInput } from "@/marketing/models/campaignInfluencer";

interface AddContentSubmissionFormProps {
  existingUrls: string[];
  disabled: boolean;
  onAdd: (submission: AdminContentSubmissionInput) => Promise<unknown>;
}

export function AddContentSubmissionForm({ existingUrls, disabled, onAdd }: AddContentSubmissionFormProps) {
  const [url, setUrl] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const trimmedUrl = url.trim();
  const canAdd = /^https:\/\/.+/i.test(trimmedUrl) && !existingUrls.includes(trimmedUrl);
  const submit = async () => {
    if (disabled || inFlight.current || !canAdd) return;
    inFlight.current = true;
    setPending(true);
    try {
      await onAdd({ url: trimmedUrl, mandatory });
      setUrl("");
      setMandatory(false);
    } catch {
      // The mutation reports the server error; keep the draft available for correction.
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-end tw-gap-2">
        <Input
          aria-label="Content link"
          value={url}
          disabled={disabled || pending}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="Paste a TikTok or Instagram video link…"
          className="tw-h-9"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <Button type="button" size="sm" className="tw-gap-1.5" disabled={disabled || pending || !canAdd} onClick={() => void submit()}>
          <Plus className="tw-h-3.5 tw-w-3.5" />
          Add
        </Button>
      </div>
      <label className="tw-flex tw-items-center tw-gap-2 tw-text-[12px]">
        <input type="checkbox" checked={mandatory} disabled={disabled || pending} onChange={(event) => setMandatory(event.target.checked)} />
        Mandatory
      </label>
    </div>
  );
}
